import { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { elakayAutoText } from "./dataElakay";

// Theme par defaut (memes valeurs que l'objet T dans imageCombiner.jsx).
// Un appelant peut passer son propre `theme` pour s'adapter a son fond
// (ex: panneau sombre) sans dupliquer ce composant.
const DEFAULT_THEME = {
  white: "#ffffff",
  border: "rgba(0,0,0,0.1)",
  borderMid: "rgba(0,0,0,0.15)",
  text: "#1a1a1a",
  muted: "#6c757d",
  light: "#f5f5f5",
  blueBg: "#e8f3ff",
  blue: "#0070cc",
  headerBg: "#fafafa",
};

/* ─── Floating draggable panel ─────────────────────────────────────────────── */
// Extrait du FloatingPanel local de imageCombiner.jsx pour etre reutilise
// ailleurs (ex: SessionExpiryWarning dans Footer.js) sans dupliquer la
// logique de glisser/redimensionner/garder-dans-le-viewport. Comportement
// inchange pour imageCombiner.jsx (memes props, meme rendu) ; showMinimize
// et theme sont les seuls ajouts, tous deux optionnels avec des defauts qui
// reproduisent exactement l'ancien composant local.
export default function DraggableFloatingPanel({
  title,
  children,
  defaultPos = { x: 100, y: 100 },
  defaultSize = {w: 285, h: 150 },
  isFloating,
  onToggleFloat,
  margins = { top: 12, bottom: 12, left: 12, right: 12 },
  showMinimize = true,
  theme = DEFAULT_THEME,
  restoreLabel,
}) {
  const showRestoreToggle = typeof onToggleFloat === "function";
  const [pos, setPos]   = useState(defaultPos);
  const [size, setSize] = useState(defaultSize);
  const [dragging, setDragging]   = useState(false);
  const [resizing, setResizing]   = useState(false);
  const [minimized, setMinimized] = useState(false);
  const dragStart = useRef({ mx: 0, my: 0, px: 0, py: 0 });
  const resizeStart = useRef({ mx: 0, my: 0, w: 0, h: 0 });

  const getViewportLimits = useCallback((panelSize) => {
    const left = Math.max(8, margins.left ?? 8);
    const right = Math.max(8, margins.right ?? 8);
    const top = Math.max(8, margins.top ?? 8);
    const bottom = Math.max(8, margins.bottom ?? 8);
    const maxWidth = Math.max(120, window.innerWidth - left - right);
    const maxHeight = Math.max(120, window.innerHeight - top - bottom);
    const width = Math.min(panelSize.w, maxWidth);
    const height = Math.min(panelSize.h, maxHeight);

    return {
      left,
      top,
      maxWidth,
      maxHeight,
      maxX: Math.max(left, window.innerWidth - right - width),
      maxY: Math.max(top, window.innerHeight - bottom - height),
    };
  }, [margins.bottom, margins.left, margins.right, margins.top]);

  const keepPanelInViewport = useCallback(() => {
    if (!isFloating) return;
    setSize(current => {
      const limits = getViewportLimits(current);
      const next = {
        w: Math.min(current.w, limits.maxWidth),
        h: Math.min(current.h, limits.maxHeight),
      };
      return next.w === current.w && next.h === current.h ? current : next;
    });
    setPos(current => {
      const limits = getViewportLimits(size);
      const next = {
        x: Math.max(limits.left, Math.min(limits.maxX, current.x)),
        y: Math.max(limits.top, Math.min(limits.maxY, current.y)),
      };
      return next.x === current.x && next.y === current.y ? current : next;
    });
  }, [getViewportLimits, isFloating, size]);

  useEffect(() => {
    if (!isFloating) return;
    keepPanelInViewport();
    window.addEventListener("resize", keepPanelInViewport);
    return () => window.removeEventListener("resize", keepPanelInViewport);
  }, [isFloating, keepPanelInViewport]);

  const onDragStart = (e) => {
    if (!isFloating) return;
    e.preventDefault();
    const t = e.touches?.[0] ?? e;
    dragStart.current = { mx: t.clientX, my: t.clientY, px: pos.x, py: pos.y };
    setDragging(true);
  };

  const onResizeStart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const t = e.touches?.[0] ?? e;
    resizeStart.current = { mx: t.clientX, my: t.clientY, w: size.w, h: size.h };
    setResizing(true);
  };

  useEffect(() => {
    if (!dragging && !resizing) return;
    const onMove = (e) => {
      const t = e.touches?.[0] ?? e;
      if (dragging) {
        const dx = t.clientX - dragStart.current.mx;
        const dy = t.clientY - dragStart.current.my;
        const limits = getViewportLimits(size);
        setPos({
          x: Math.max(limits.left, Math.min(limits.maxX, dragStart.current.px + dx)),
          y: Math.max(limits.top, Math.min(limits.maxY, dragStart.current.py + dy)),
        });
      } else if (resizing) {
        const dx = t.clientX - resizeStart.current.mx;
        const dy = t.clientY - resizeStart.current.my;
        const limits = getViewportLimits(size);
        setSize({
          w: Math.min(limits.maxWidth, Math.max(220, resizeStart.current.w + dx)),
          h: Math.min(limits.maxHeight, Math.max(160, resizeStart.current.h + dy)),
        });
      }
    };
    const onUp = () => { setDragging(false); setResizing(false); };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onMove, { passive: false });
    window.addEventListener("touchend", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
    };
  }, [dragging, resizing, getViewportLimits, size]);

  const containerStyle = isFloating ? {
    position: "fixed",
    left: pos.x, top: pos.y,
    width: size.w, height: minimized ? "auto" : size.h,
    zIndex: 9999,
    background: theme.white,
    border: `1px solid ${theme.borderMid}`,
    borderRadius: 10,
    boxShadow: "0 12px 40px rgba(0,0,0,0.25)",
    display: "flex", flexDirection: "column",
    overflow: "hidden",
    resize: "none",
  } : {
    width: "100%",
    background: "transparent",
  };

  const panel = (
    <div style={containerStyle}>
      {/* Title bar — only visible when floating */}
      {isFloating && (
        <div
          onMouseDown={onDragStart}
          onTouchStart={onDragStart}
          style={{
            display: "flex", alignItems: "center", gap: 8,
            padding: "8px 12px",
            background: theme.headerBg,
            borderBottom: `0.5px solid ${theme.border}`,
            cursor: dragging ? "grabbing" : "grab",
            userSelect: "none",
            flexShrink: 0,
          }}
        >
          <span style={{ flex: 1, fontSize: 12, fontWeight: 600, color: theme.text }}>
            ⋮⋮ {title}
          </span>
          {showMinimize && (
            <button
              onClick={() => setMinimized(m => !m)}
              onMouseDown={e => e.stopPropagation()}
              onTouchStart={e => e.stopPropagation()}
              style={{
                width: 22, height: 22, borderRadius: 4, border: "none",
                background: theme.light, cursor: "pointer", fontSize: 11,
                color: theme.muted,
              }}
              title={minimized ? elakayAutoText("h_0811e108abbdbd", "Agrandir") : elakayAutoText("h_86a5250550e63f", "Réduire")}
            >{minimized ? "▢" : "_"}</button>
          )}
          {showRestoreToggle && (
            <button
              onClick={onToggleFloat}
              onMouseDown={e => e.stopPropagation()}
              onTouchStart={e => e.stopPropagation()}
              style={{
                width: 22, height: 22, borderRadius: 4, border: "none",
                background: theme.blueBg, cursor: "pointer", fontSize: 11,
                color: theme.blue,
              }}
              title={restoreLabel || elakayAutoText("h_3cbe6d6b9a8d15", "Restore")}
            >⇲</button>
          )}
        </div>
      )}

      {/* Content */}
      {!minimized && (
        <div style={{
          flex: 1,
          overflow: isFloating ? "auto" : "visible",
          padding: isFloating ? 8 : 0,
          position: "relative",
        }}>
          {children}

          {/* Resize handle */}
          {isFloating && (
            <div
              onMouseDown={onResizeStart}
              onTouchStart={onResizeStart}
              style={{
                position: "absolute", right: 0, bottom: 0,
                width: 18, height: 18, cursor: "nwse-resize",
                background: `linear-gradient(135deg, transparent 50%, ${theme.borderMid} 50%, ${theme.borderMid} 60%, transparent 60%, transparent 75%, ${theme.borderMid} 75%, ${theme.borderMid} 85%, transparent 85%)`,
              }}
            />
          )}
        </div>
      )}
    </div>
  );

  return isFloating && typeof document !== "undefined"
    ? createPortal(panel, document.body)
    : panel;
}
